import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faFilter,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { config } from "../../config";

const Filters = ({ results, setFilteredResults }) => {
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  // Filter states
  const [priceRange, setPriceRange] = useState({ min: "", max: "" });
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedRatings, setSelectedRatings] = useState([]);
  const [selectedReviewScores, setSelectedReviewScores] = useState([]);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [selectedChains, setSelectedChains] = useState([]);
  const [showAllAmenities, setShowAllAmenities] = useState(false);

  // Visibility states
  const [isPriceVisible, setPriceVisible] = useState(true);
  const [isCitiesVisible, setCitiesVisible] = useState(false);
  const [isRatingsVisible, setRatingsVisible] = useState(false);
  const [isReviewScoresVisible, setReviewScoresVisible] = useState(false);
  const [isAmenitiesVisible, setAmenitiesVisible] = useState(false);
  const [isChainsVisible, setChainsVisible] = useState(false);

  // Add these new state variables at the top with other states
  const [sortOption, setSortOption] = useState("recommended");
  const [isSortVisible, setSortVisible] = useState(true);

  // Extract unique values from results
  useEffect(() => {
    if (!results?.length) return;

    let filtered = results.filter((result) => {
      const price = parseFloat(result.cheapest_rate_total_amount);
      const meetsPrice =
        (!priceRange.min || price >= parseFloat(priceRange.min)) &&
        (!priceRange.max || price <= parseFloat(priceRange.max));

      const meetsCity =
        selectedCities.length === 0 ||
        selectedCities.includes(
          result.accommodation.location.address.city_name
        );

      const meetsRating =
        selectedRatings.length === 0 ||
        selectedRatings.includes(result.accommodation.rating);

      const meetsReviewScore =
        selectedReviewScores.length === 0 ||
        selectedReviewScores.some((range) => {
          const [min, max] = range.split("-").map(Number);
          return (
            result.accommodation.review_score >= min &&
            result.accommodation.review_score <= max
          );
        });

      const meetsAmenities =
        selectedAmenities.length === 0 ||
        selectedAmenities.every((amenity) =>
          result.accommodation.amenities.some((a) => a.description === amenity)
        );

      const meetsChain =
        selectedChains.length === 0 ||
        selectedChains.includes(result.accommodation.chain?.name);

      return (
        meetsPrice &&
        meetsCity &&
        meetsRating &&
        meetsReviewScore &&
        meetsAmenities &&
        meetsChain
      );
    });

    // Sorting logic
    if (sortOption === "price") {
      filtered.sort((a, b) => {
        const priceA = parseFloat(a.cheapest_rate_total_amount || "9999999");
        const priceB = parseFloat(b.cheapest_rate_total_amount || "9999999");
        return priceA - priceB;
      });
    } else if (sortOption === "review") {
      filtered.sort((a, b) => {
        const scoreA = b.accommodation.review_score || 0;
        const scoreB = a.accommodation.review_score || 0;
        return scoreA - scoreB;
      });
    } else {
      filtered.sort((a, b) => results.indexOf(a) - results.indexOf(b));
    }

    setFilteredResults(filtered);
  }, [
    results,
    priceRange,
    selectedCities,
    selectedRatings,
    selectedReviewScores,
    selectedAmenities,
    selectedChains,
    sortOption,
    setFilteredResults,
  ]);

  // Get unique values for filter options
  const getUniqueValues = () => {
    if (!results?.length) return {};

    const cities = [
      ...new Set(
        results.map((r) => r.accommodation.location.address.city_name)
      ),
    ];
    const ratings = [...new Set(results.map((r) => r.accommodation.rating))];
    const amenities = [
      ...new Set(
        results.flatMap((r) =>
          r.accommodation.amenities.map((a) => a.description)
        )
      ),
    ];
    const chains = [
      ...new Set(
        results.map((r) => r.accommodation.chain?.name).filter(Boolean)
      ),
    ];

    return { cities, ratings, amenities, chains };
  };

  const { cities, ratings, amenities, chains } = getUniqueValues();

  const reviewScoreRanges = ["0-2", "2-4", "4-6", "6-8", "8-10"];

  return (
    <>
      {/* Mobile Filter Button */}
      <div className="md:hidden fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
          className={`${config.primaryColorBg} text-white p-4 rounded-full shadow-lg`}
        >
          <FontAwesomeIcon icon={isMobileFilterOpen ? faTimes : faFilter} />
        </button>
      </div>

      {/* Filters Panel */}
      <div
        className={`bg-white p-4 space-y-4 ${
          isMobileFilterOpen
            ? "fixed inset-0 z-50 overflow-y-auto"
            : "hidden md:block"
        }`}
      >

        {/* Mobile Close Button */}
        {isMobileFilterOpen && (
          <div className="flex justify-end md:hidden">
            <button
              onClick={() => setIsMobileFilterOpen(false)}
              className="p-2 text-gray-500 hover:text-gray-700"
            >
              <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
            </button>
          </div>
        )}
        {/* Sort By */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900">Sort By</h3>
            <button onClick={() => setSortVisible(!isSortVisible)}>
              <FontAwesomeIcon
                icon={isSortVisible ? faChevronUp : faChevronDown}
              />
            </button>
          </div>
          {isSortVisible && (
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="sortOption"
                  value="recommended"
                  checked={sortOption === "recommended"}
                  onChange={(e) => setSortOption(e.target.value)}
                  className="form-radio text-indigo-600"
                />
                <span className="ml-2 text-sm text-gray-700">Recommended</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="sortOption"
                  value="price"
                  checked={sortOption === "price"}
                  onChange={(e) => setSortOption(e.target.value)}
                  className="form-radio text-indigo-600"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Price (lowest first)
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="sortOption"
                  value="review"
                  checked={sortOption === "review"}
                  onChange={(e) => setSortOption(e.target.value)}
                  className="form-radio text-indigo-600"
                />
                <span className="ml-2 text-sm text-gray-700">Review Score</span>
              </label>
            </div>
          )}
        </div>

        {/* Price Range */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900">Price Range</h3>
            <button onClick={() => setPriceVisible(!isPriceVisible)}>
              <FontAwesomeIcon
                icon={isPriceVisible ? faChevronUp : faChevronDown}
              />
            </button>
          </div>
          {isPriceVisible && (
            <div className="flex gap-2">
              <input
                type="number"
                placeholder="Min"
                value={priceRange.min}
                onChange={(e) =>
                  setPriceRange({ ...priceRange, min: e.target.value })
                }
                className="w-1/2 p-2 border rounded"
              />
              <input
                type="number"
                placeholder="Max"
                value={priceRange.max}
                onChange={(e) =>
                  setPriceRange({ ...priceRange, max: e.target.value })
                }
                className="w-1/2 p-2 border rounded"
              />
            </div>
          )}
        </div>

        {/* Cities */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900">Cities</h3>
            <button onClick={() => setCitiesVisible(!isCitiesVisible)}>
              <FontAwesomeIcon
                icon={isCitiesVisible ? faChevronUp : faChevronDown}
              />
            </button>
          </div>
          {isCitiesVisible && (
            <div className="space-y-2">
              {cities.map((city) => (
                <label key={city} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedCities.includes(city)}
                    onChange={(e) => {
                      setSelectedCities(
                        e.target.checked
                          ? [...selectedCities, city]
                          : selectedCities.filter((c) => c !== city)
                      );
                    }}
                    className="form-checkbox text-indigo-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">{city}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Hotel Rating */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900">
              Hotel Rating
            </h3>
            <button onClick={() => setRatingsVisible(!isRatingsVisible)}>
              <FontAwesomeIcon
                icon={isRatingsVisible ? faChevronUp : faChevronDown}
              />
            </button>
          </div>
          {isRatingsVisible && (
            <div className="space-y-2">
              {ratings.sort().map((rating) => (
                <label key={rating} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedRatings.includes(rating)}
                    onChange={(e) => {
                      setSelectedRatings(
                        e.target.checked
                          ? [...selectedRatings, rating]
                          : selectedRatings.filter((r) => r !== rating)
                      );
                    }}
                    className="form-checkbox text-indigo-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    {rating} Stars
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Review Scores */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900">
              Review Scores
            </h3>
            <button
              onClick={() => setReviewScoresVisible(!isReviewScoresVisible)}
            >
              <FontAwesomeIcon
                icon={isReviewScoresVisible ? faChevronUp : faChevronDown}
              />
            </button>
          </div>
          {isReviewScoresVisible && (
            <div className="space-y-2">
              {reviewScoreRanges.map((range) => (
                <label key={range} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedReviewScores.includes(range)}
                    onChange={(e) => {
                      setSelectedReviewScores(
                        e.target.checked
                          ? [...selectedReviewScores, range]
                          : selectedReviewScores.filter((r) => r !== range)
                      );
                    }}
                    className="form-checkbox text-indigo-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">{range}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Amenities */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900">Amenities</h3>
            <button onClick={() => setAmenitiesVisible(!isAmenitiesVisible)}>
              <FontAwesomeIcon
                icon={isAmenitiesVisible ? faChevronUp : faChevronDown}
              />
            </button>
          </div>
          {isAmenitiesVisible && (
            <div className="space-y-2">
              {amenities
                .slice(0, showAllAmenities ? undefined : 3)
                .map((amenity) => (
                  <label key={amenity} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedAmenities.includes(amenity)}
                      onChange={(e) => {
                        setSelectedAmenities(
                          e.target.checked
                            ? [...selectedAmenities, amenity]
                            : selectedAmenities.filter((a) => a !== amenity)
                        );
                      }}
                      className="form-checkbox text-indigo-600"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      {amenity}
                    </span>
                  </label>
                ))}
              {amenities.length > 3 && (
                <button
                  onClick={() => setShowAllAmenities(!showAllAmenities)}
                  className="text-sm text-blue-400 hover:text-indigo-800"
                >
                  {showAllAmenities ? "Show Less" : "Show More"}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Hotel Chains */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900">
              Hotel Chains
            </h3>
            <button onClick={() => setChainsVisible(!isChainsVisible)}>
              <FontAwesomeIcon
                icon={isChainsVisible ? faChevronUp : faChevronDown}
              />
            </button>
          </div>
          {isChainsVisible && (
            <div className="space-y-2">
              {chains.map((chain) => (
                <label key={chain} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedChains.includes(chain)}
                    onChange={(e) => {
                      setSelectedChains(
                        e.target.checked
                          ? [...selectedChains, chain]
                          : selectedChains.filter((c) => c !== chain)
                      );
                    }}
                    className="form-checkbox text-indigo-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">{chain}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Filters;
